import { useCallback, useEffect, useRef } from "react";
import { NAV_LINKS, HERO, SOCIALS } from "../data";

export default function Nav({
  scrolled,
  menuOpen,
  onToggleMenu,
  onNavigate,
  activeSection,
}) {
  const drawerRef = useRef(null);
  const hamburgerRef = useRef(null);

  // ── Body scroll lock ────────────────────────────────────────────────────────
  // Captures scrollY at the moment of opening so the closure can restore it on
  // close. Uses position:fixed + top:-Npx for iOS Safari (overflow:hidden alone
  // does not prevent touch-scroll there). Compensates scrollbar width to prevent
  // layout shift on desktop.
  useEffect(() => {
    if (!menuOpen) return;

    const scrollY = window.scrollY;
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.paddingRight = "";
      window.scrollTo(0, scrollY);
    };
  }, [menuOpen]);

  // ── Escape key ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onToggleMenu();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen, onToggleMenu]);

  // ── Focus management ────────────────────────────────────────────────────────
  useEffect(() => {
    if (menuOpen) {
      drawerRef.current
        ?.querySelector("button:not([disabled]), [href]")
        ?.focus();
    } else {
      hamburgerRef.current?.focus();
    }
  }, [menuOpen]);

  // ── Focus trap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!menuOpen) return;
    const el = drawerRef.current;
    if (!el) return;

    const onTab = (e) => {
      if (e.key !== "Tab") return;
      const focusable = [
        ...el.querySelectorAll(
          "button:not([disabled]), [href], [tabindex]:not([tabindex='-1'])"
        ),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", onTab);
    return () => document.removeEventListener("keydown", onTab);
  }, [menuOpen]);

  const handleNavClick = useCallback(
    (label) => onNavigate(label),
    [onNavigate]
  );

  return (
    <>
      {/* ── Top nav bar ──────────────────────────────────────────────────────── */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-slate-950/95 border-b border-slate-800 backdrop-blur-xl"
            : "bg-transparent"
        }`}
        aria-label="Main navigation"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
          {/* Logo */}
          <a
            className="rounded text-xl font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-fuchsia-400 to-cyan-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500"
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              onNavigate("Hero");
            }}
          >
            SK.
          </a>

          {/* Desktop links */}
          <div className="hidden items-center gap-7 md:flex">
            {NAV_LINKS.map((label) => {
              const isActive = activeSection === label.toLowerCase();
              return (
                <button
                  key={label}
                  type="button"
                  className={`flex flex-col items-center gap-0.5 rounded px-1 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                    isActive ? "text-slate-100" : "text-slate-400 hover:text-slate-100"
                  }`}
                  onClick={() => onNavigate(label)}
                >
                  {label}
                  {/* Animated active underline */}
                  <span
                    aria-hidden="true"
                    className={`block h-px rounded-full bg-gradient-to-r from-fuchsia-400 to-cyan-400 transition-all duration-300 origin-left ${
                      isActive ? "w-full opacity-100" : "w-0 opacity-0"
                    }`}
                  />
                </button>
              );
            })}
            <a
              href="mailto:shubham1bcabnc18@gmail.com"
              className="rounded-full border border-fuchsia-500/40 px-4 py-2 text-sm font-semibold text-fuchsia-300 transition-colors duration-200 hover:bg-fuchsia-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500"
            >
              Hire Me
            </a>
          </div>

          {/* Hamburger — animated CSS transform, never unmounted */}
          <button
            ref={hamburgerRef}
            type="button"
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-drawer"
            className="relative inline-flex h-11 w-11 flex-col items-center justify-center gap-1.5 rounded-full border border-slate-800 bg-slate-950/90 transition-colors duration-200 hover:border-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500 md:hidden"
            onClick={onToggleMenu}
          >
            <span
              aria-hidden="true"
              className={`block h-0.5 w-5 origin-center bg-slate-100 transition-transform duration-300 ease-in-out ${
                menuOpen ? "translate-y-2 rotate-45" : ""
              }`}
            />
            <span
              aria-hidden="true"
              className={`block h-0.5 w-5 bg-slate-100 transition-all duration-200 ease-in-out ${
                menuOpen ? "scale-x-0 opacity-0" : "opacity-100"
              }`}
            />
            <span
              aria-hidden="true"
              className={`block h-0.5 w-5 origin-center bg-slate-100 transition-transform duration-300 ease-in-out ${
                menuOpen ? "-translate-y-2 -rotate-45" : ""
              }`}
            />
          </button>
        </div>
      </nav>

      {/*
        ── Mobile drawer ─────────────────────────────────────────────────────────
        Always mounted in the DOM — never conditionally rendered.

        WHY: `menuOpen && <div>` would fully unmount this subtree on close and
        remount it on open. Every remount forces the browser to recreate GPU
        compositing layers for backdrop-blur and alpha-blended backgrounds. On
        Safari and mid-range Android Chrome this layer recreation has a timing bug
        that causes `bg-slate-900/95` elements to render transparent for one or
        more frames — producing the "lost styles" flicker after repeated cycles.
        Keeping it mounted and toggling via CSS eliminates the bug entirely.

        Hidden via:
          • `inert`              — removes keyboard focus, pointer events, aria tree
          • `opacity-0`         — GPU-accelerated visual fade
          • `pointer-events-none` — belt-and-suspenders for pre-inert browsers

        z-[60] > nav z-50 so the drawer fully covers the nav bar.
      */}
      <div
        id="mobile-nav-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        {...(!menuOpen && { inert: "" })}
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${
          menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop — tapping outside closes the drawer */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
          onClick={onToggleMenu}
        />

        {/* Panel — slides in from the right */}
        <div
          className={`absolute right-0 top-0 flex h-full w-[85vw] max-w-[340px] flex-col bg-slate-950 shadow-2xl shadow-black/50 transition-transform duration-300 ease-out ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Panel header */}
          <div className="flex shrink-0 items-center justify-between border-b border-slate-800/80 px-5 py-4">
            <span className="text-base font-black tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-fuchsia-400 to-cyan-400">
              SK.
            </span>
            <button
              type="button"
              aria-label="Close navigation menu"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-800 bg-slate-900/80 text-slate-400 transition-colors duration-200 hover:border-slate-700 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500"
              onClick={onToggleMenu}
            >
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M1 1L11 11M11 1L1 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          {/* ── Scrollable body ──────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto">
            {/* Profile card — stagger item 0 */}
            <div
              style={{ transitionDelay: menuOpen ? "80ms" : "0ms" }}
              className={`border-b border-slate-800/60 px-5 py-5 transition-all duration-300 ${
                menuOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-fuchsia-500 to-cyan-400 text-xs font-black text-slate-950">
                  SK
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-100">
                    {HERO.firstName} {HERO.lastName}
                  </p>
                  <p className="truncate text-xs text-slate-400">{HERO.title}</p>
                </div>
              </div>
              <div className="mt-3.5 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-400">
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse-dot"
                />
                Open to Work
              </div>
            </div>

            {/* Nav links — stagger items 1–N */}
            <nav aria-label="Mobile navigation" className="space-y-1 p-3">
              {NAV_LINKS.map((label, index) => {
                const isActive = activeSection === label.toLowerCase();
                return (
                  <button
                    key={label}
                    type="button"
                    style={{
                      transitionDelay: menuOpen ? `${100 + index * 35}ms` : "0ms",
                    }}
                    className={`flex w-full items-center rounded-xl border px-4 py-3.5 text-left text-sm font-semibold transition-all duration-300 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fuchsia-500 ${
                      menuOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                    } ${
                      isActive
                        ? "border-fuchsia-500/25 bg-fuchsia-500/10 text-white"
                        : "border-slate-800/60 bg-slate-900/40 text-slate-300 hover:border-slate-700 hover:bg-slate-900/80 hover:text-slate-100"
                    }`}
                    onClick={() => handleNavClick(label)}
                  >
                    <span className="flex-1">{label}</span>
                    {isActive && (
                      <span
                        aria-hidden="true"
                        className="ml-2 h-1.5 w-1.5 shrink-0 rounded-full bg-fuchsia-400"
                      />
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Social links — stagger item N+1 */}
            <div
              style={{
                transitionDelay: menuOpen
                  ? `${100 + NAV_LINKS.length * 35 + 30}ms`
                  : "0ms",
              }}
              className={`border-t border-slate-800/60 px-4 py-4 transition-all duration-300 ${
                menuOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
              }`}
            >
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-500">
                Connect
              </p>
              <div className="flex gap-2">
                {SOCIALS.map((social) => (
                  <a
                    key={social.label}
                    href={social.link}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.label}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-800/60 bg-slate-900/40 text-base transition-colors duration-200 hover:border-slate-700 hover:bg-slate-900/80 active:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500"
                    title={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* ── Fixed CTAs at bottom — always visible ──────────────────────── */}
          <div
            style={{
              transitionDelay: menuOpen
                ? `${100 + NAV_LINKS.length * 35 + 70}ms`
                : "0ms",
            }}
            className={`shrink-0 space-y-2 border-t border-slate-800/80 p-4 transition-all duration-300 ${
              menuOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            <a
              href="mailto:shubham1bcabnc18@gmail.com"
              className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-400 px-5 py-3 text-sm font-bold text-slate-950 transition-opacity duration-200 hover:opacity-90 active:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400"
            >
              Hire Me
            </a>
            <a
              href="#"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700/60 bg-slate-900/40 px-5 py-3 text-sm font-semibold text-slate-300 transition-colors duration-200 hover:border-slate-600 hover:bg-slate-900/80 hover:text-slate-100 active:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
            >
              <svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path
                  d="M6.5 1v7.5M3 5.5l3.5 3.5 3.5-3.5M1 11.5h11"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Resume
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
